import {
	animate,
	state,
	style,
	transition,
	trigger
} from '@angular/animations';
import { NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { StorageKey } from '@brand-lolly/app/_models/local-storage-key';
import { freeAllowanceRenewsLabel } from '@brand-lolly/app/billing/types/credit-statement.types';
import {
	CreditBalanceSnapshot,
	CreditBalanceStateService
} from '@brand-lolly/app/billing/services/credit-balance-state.service';
import {
	CloseOverlayButtonComponent,
	PrimaryButtonComponent,
	TypographyComponent
} from 'components';
import { ToastNotificationService } from '@brand-lolly/app/shared/toast-notification/toast-notification.service';
import { SeatCreditRequestStateService } from '@brand-lolly/app/user-management/team/services/seat-credit-request-state.service';
import { TeamPermissionsStateService } from '@brand-lolly/app/user-management/team/services/team-permissions-state.service';
import {
	ASK_FOR_CREDITS_LABEL,
	askedForCreditsNote,
	askRefusalSentence
} from '@brand-lolly/app/user-management/team/types/credit-request.util';
import {
	CreditRequest,
	TeamAction
} from '@brand-lolly/app/user-management/team/types/team.types';
import { filter, Subject, takeUntil } from 'rxjs';
import { CREDITS_STATEMENT_ROUTE } from '@brand-lolly/components/credit-balance-indicator/credit-balance-indicator.component';

const UPGRADE_ROUTE = '/user-management/manage-plan';

/**
 * The low-credit warning, hung under the balance chip in the application header.
 *
 * **It used to be a full-width bar wedged between the header and the page**, and
 * that shape was most of what was wrong with it: it pushed every screen down as
 * it appeared, it was the widest and loudest thing on any page it landed on, and
 * it said nothing about where the balance it was talking about lives. It is now
 * a popover anchored to that balance — it slides down out of the chip, points at
 * it, overlays rather than displaces, and is the size of the sentence it carries.
 * Same three states, same copy contract, same dismissal rule.
 *
 * A dismissal is NOT permanent, and that is the whole point of the stored
 * figure: what is remembered is the balance the brand dismissed AT, so the
 * warning returns the moment the balance falls below it. Suppressing it for a
 * session or forever would mean the one brand who most needs telling — the one
 * still spending — is the one who stops being told.
 *
 * It stays quiet on the credit statement screen itself. That page states the
 * same thing at length, with the figures and the route out, and a popover
 * repeating it over the top is the duplicate the brand was looking at when it
 * called this notification eye-gouging.
 */
@Component({
	standalone: true,
	selector: 'app-credit-low-balance-banner',
	templateUrl: './credit-low-balance-banner.component.html',
	styleUrls: ['./credit-low-balance-banner.component.scss'],
	imports: [
		NgIf,
		TypographyComponent,
		PrimaryButtonComponent,
		CloseOverlayButtonComponent
	],
	animations: [
		// Out of the chip, not onto the page: it starts a little above its
		// resting place and slightly small, so it reads as coming from the
		// balance rather than appearing over it. Ease-out only — nothing here
		// overshoots, because a warning that bounces reads as a toy.
		trigger('slideFromChip', [
			state(
				'void',
				style({
					opacity: 0,
					transform: 'translateY(-0.5rem) scale(0.97)'
				})
			),
			state(
				'*',
				style({ opacity: 1, transform: 'translateY(0) scale(1)' })
			),
			transition('void => *', [
				animate('240ms cubic-bezier(0.16, 1, 0.3, 1)')
			]),
			transition('* => void', [
				animate('140ms cubic-bezier(0.4, 0, 1, 1)')
			])
		])
	]
})
export class CreditLowBalanceBannerComponent implements OnInit, OnDestroy {
	show = false;
	/** True for the out-of-credits state, which reads differently from "low". */
	exhausted = false;
	title = '';
	body = '';
	actionLabel = '';
	/**
	 * Whether the action button is drawn at all.
	 *
	 * Both of its labels — See plans and Buy credits — spend the ACCOUNT's
	 * money, so a seated member is warned and shown the statement, and is not
	 * offered the purchase. Telling them their balance is low is still worth
	 * doing: what they do about it is ask the owner.
	 */
	canManageBilling = true;
	/**
	 * A member who may ask the master account for credits — where the owner's
	 * purchase button would be, this is what they are offered instead.
	 */
	canAsk = false;
	readonly askLabel = ASK_FOR_CREDITS_LABEL;
	askInFlight = false;
	/** "Master account asked · 5 minutes ago", once they have. Replaces the control. */
	askedNote = '';
	/**
	 * Whether the member's own request has been read. Until it has, neither
	 * the control nor the note is shown — the slot keeps its size — so a
	 * member who has asked is never offered to ask again for a moment.
	 */
	requestLoaded = false;
	/** A free account is sent to plans; a paying one is sent to a top-up. */
	private upgradePath = false;
	/** The statement screen says all of this itself, in more detail. */
	private onStatementScreen = false;
	private snapshot: CreditBalanceSnapshot | null = null;
	private openRequest: CreditRequest | null = null;

	private unsubscriber = new Subject<void>();

	constructor(
		private creditBalanceState: CreditBalanceStateService,
		private router: Router,
		private permissionsState: TeamPermissionsStateService,
		private creditRequestState: SeatCreditRequestStateService,
		private toast: ToastNotificationService
	) {}

	ngOnInit(): void {
		this.onStatementScreen = isStatementScreen(this.router.url);
		this.router.events
			.pipe(
				filter(
					(event): event is NavigationEnd =>
						event instanceof NavigationEnd
				),
				takeUntil(this.unsubscriber)
			)
			.subscribe(event => {
				this.onStatementScreen = isStatementScreen(
					event.urlAfterRedirects
				);
				if (this.snapshot !== null) {
					this.applySnapshot(this.snapshot);
				}
			});
		this.creditBalanceState.snapshot$
			.pipe(takeUntil(this.unsubscriber))
			.subscribe(snapshot => this.applySnapshot(snapshot));
		this.permissionsState
			.ensureLoaded()
			.pipe(takeUntil(this.unsubscriber))
			.subscribe(() => {
				this.canManageBilling = this.permissionsState.can(
					TeamAction.BILLING_MANAGE
				);
				this.canAsk =
					!this.canManageBilling &&
					this.permissionsState.can(TeamAction.CREDIT_REQUEST_RAISE);
				if (this.canAsk) {
					this.creditRequestState
						.ensureLoaded()
						.pipe(takeUntil(this.unsubscriber))
						.subscribe();
					// This popover lives in the shell for the whole session, so it is
					// what keeps an open request re-checked on every screen — and the
					// balance re-read once the master account has answered it.
					this.creditRequestState.watch$
						.pipe(takeUntil(this.unsubscriber))
						.subscribe();
				}
			});
		this.creditRequestState.loaded$
			.pipe(takeUntil(this.unsubscriber))
			.subscribe(loaded => (this.requestLoaded = loaded));
		this.creditRequestState.request$
			.pipe(takeUntil(this.unsubscriber))
			.subscribe(request => {
				this.openRequest = request;
				this.refreshAskedNote();
			});
	}

	ngOnDestroy(): void {
		this.unsubscriber.next();
		this.unsubscriber.complete();
	}

	act(): void {
		if (this.upgradePath) {
			this.router.navigate([UPGRADE_ROUTE], {
				queryParams: { openPlans: true }
			});
			return;
		}
		this.router.navigate([CREDITS_STATEMENT_ROUTE], {
			queryParams: { topup: 'open' }
		});
	}

	ask(): void {
		if (this.askInFlight) {
			return;
		}
		this.askInFlight = true;
		this.creditRequestState
			.ask()
			.pipe(takeUntil(this.unsubscriber))
			.subscribe({
				next: () => (this.askInFlight = false),
				error: error => {
					this.askInFlight = false;
					this.toast.sendErrorToast(askRefusalSentence(error));
				}
			});
	}

	openStatement(): void {
		this.router.navigate([CREDITS_STATEMENT_ROUTE]);
	}

	dismiss(): void {
		// Remember the balance, not the fact of dismissing: the next read
		// compares against it and shows the warning again once it drops below.
		writeDismissedAt(this.creditBalanceState.snapshot.spendableMilli);
		this.show = false;
	}

	private applySnapshot(snapshot: CreditBalanceSnapshot): void {
		this.snapshot = snapshot;
		// A failed read is not an empty balance — say nothing rather than warn
		// a paying brand about a balance nobody could read.
		if (!snapshot.loaded || snapshot.failed) {
			this.show = false;
			return;
		}
		if (!snapshot.low && !snapshot.exhausted) {
			// Comfortable again: forget the dismissal so the next descent
			// warns from scratch instead of being measured against an old,
			// much lower figure.
			clearDismissedAt();
			this.show = false;
			return;
		}

		const dismissedAt = readDismissedAt();
		this.show =
			!this.onStatementScreen &&
			(dismissedAt === null || snapshot.spendableMilli < dismissedAt);
		if (!this.show) {
			return;
		}

		this.refreshAskedNote();
		this.exhausted = snapshot.exhausted;
		this.upgradePath = snapshot.bounded;
		this.actionLabel = snapshot.bounded ? 'See plans' : 'Buy credits';
		this.title = buildTitle(snapshot);
		this.body = buildBody(snapshot);
	}

	/** Re-derived whenever the popover is shown, so "5 minutes ago" does not go stale. */
	private refreshAskedNote(): void {
		this.askedNote = this.openRequest
			? askedForCreditsNote(this.openRequest)
			: '';
	}
}

/** The statement screen, with or without the top-up query parameter. */
function isStatementScreen(url: string): boolean {
	return url.split('?')[0] === CREDITS_STATEMENT_ROUTE;
}

function buildTitle(snapshot: CreditBalanceSnapshot): string {
	if (snapshot.exhausted) {
		return snapshot.bounded
			? 'Your free credits are used up'
			: 'You have run out of credits';
	}
	return snapshot.bounded
		? 'Your free credits are running low'
		: 'Your credit balance is running low';
}

function buildBody(snapshot: CreditBalanceSnapshot): string {
	const left = formatCredits(snapshot.spendableCredits);
	if (snapshot.exhausted) {
		return snapshot.bounded
			? `This month's free credits renew on ${freeAllowanceRenewsLabel()}. Until then creator lookups, AI search and outreach stop — buy credits or choose a plan to keep going. Everything already collected stays where it is.`
			: 'Creator lookups, AI search and outreach stop until you top up. Everything already collected stays where it is.';
	}
	if (snapshot.bounded && snapshot.ceilingCredits !== null) {
		return `${left} of your ${formatCredits(
			snapshot.ceilingCredits
		)} free credits left this month · renews ${freeAllowanceRenewsLabel()}. Choose a plan to keep creator lookups, AI search and outreach running.`;
	}
	return `${left} credits are left. Top up to keep creator lookups, AI search and outreach running.`;
}

function formatCredits(credits: number): string {
	const fractionDigits = Number.isInteger(credits) ? 0 : 2;
	return credits.toLocaleString('en-US', {
		minimumFractionDigits: fractionDigits,
		maximumFractionDigits: fractionDigits
	});
}

/**
 * The balance the warning was last dismissed at, in milli-credits.
 *
 * Per browser rather than per brand, like every other dismissal key in this app.
 * Two brands sharing one browser can only ever suppress each other's warning
 * until the balance moves — which the next spend does, because the comparison is
 * against a figure and not a flag.
 */
function readDismissedAt(): number | null {
	try {
		const raw = localStorage.getItem(
			StorageKey.creditLowBalanceDismissedAt
		);
		if (raw === null) {
			return null;
		}
		const parsed = Number(raw);
		return Number.isFinite(parsed) ? parsed : null;
	} catch {
		return null;
	}
}

function writeDismissedAt(milli: number): void {
	try {
		localStorage.setItem(
			StorageKey.creditLowBalanceDismissedAt,
			String(milli)
		);
	} catch {
		// A browser refusing storage costs the brand a warning it will see
		// again on the next read — not a broken screen.
	}
}

function clearDismissedAt(): void {
	try {
		localStorage.removeItem(StorageKey.creditLowBalanceDismissedAt);
	} catch {
		// See writeDismissedAt.
	}
}
